import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Tag, Table, Button, Tabs, 
         Empty, Progress, Spin, message, Modal } from 'antd';
import { TrophyOutlined, FormOutlined, FileTextOutlined,
         PlusOutlined, BarChartOutlined } from '@ant-design/icons';
import ProgramsService from '../../services/ProgramsService';
import SurveyService from '../../services/SurveyService';
import QoCSurveyForm from './QoCSurveyForm';
import util from '../../utils/util';

const BANDS = {
    'Best Practice Champion': '#16a34a',
    'Strong Performer':       '#1890ff',
    'Developing Performer':   '#d97706',
    'Needs Support':          '#dc2626',
};

export default function QoCProgramProfile({ match }) {
    const programId = match?.params?.id;
    const [program,     setProgram]     = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [surveys,     setSurveys]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [activeTab,   setActiveTab]   = useState('overview');
    const [surveyModal, setSurveyModal] = useState(false);
    const [activeSurvey, setActiveSurvey] = useState(null);

    useEffect(() => {
        if (programId) loadAll();
    }, [programId]); // eslint-disable-line

    const loadAll = async () => {
        setLoading(true);
        try {
            const [pRes, aRes, sRes] = await Promise.all([
                ProgramsService.getPrograms(),
                ProgramsService.getAssessments({ program_id: programId }),
                SurveyService.getSurveys({ program_id: programId }),
            ]);
            const progs = pRes.data?.data || [];
            setProgram(progs.find(p => String(p.id) === String(programId)));
            setAssessments(aRes.data?.data || []);
            setSurveys(sRes.data?.data || []);
        } catch(e) { message.error(e.message); }
        finally { setLoading(false); }
    };

    const openSurvey = (survey) => {
        setActiveSurvey(survey);
        setSurveyModal(true);
    };

    if (loading) return <div style={{textAlign:'center',padding:80}}><Spin size="large"/></div>;
    if (!program) return <div style={{padding:40,textAlign:'center',color:'#888'}}>Programme not found</div>;

    const latestAssessment = assessments.find(a => a.status === 'approved') || assessments[0];
    const score = parseFloat(latestAssessment?.overall_percentage || 0);
    const band  = latestAssessment?.program_band || '—';
    const bandColor = BANDS[band] || '#888';

    const assessmentCols = [
        { title:'Period', dataIndex:'period_label', width:130 },
        { title:'Assessor', dataIndex:'assessor_name', width:150 },
        { title:'Date', dataIndex:'assessment_date', width:120 },
        { title:'Score', dataIndex:'overall_percentage', width:100,
            render: v => <span style={{fontWeight:700,color:'#1890ff'}}>{parseFloat(v||0).toFixed(1)}%</span>
        },
        { title:'Band', dataIndex:'program_band', width:180,
            render: v => v ? <Tag color={BANDS[v]?'':'default'} style={{background:BANDS[v],color:'#fff',border:'none'}}>{v}</Tag> : '—'
        },
        { title:'Status', dataIndex:'status', width:120,
            render: v => <Tag color={v==='approved'?'green':v==='submitted'?'blue':'orange'}>{v}</Tag>
        },
    ];

    const surveyCols = [
        { title:'Survey', dataIndex:'title', render:(t,r) => (
            <div>
                <div style={{fontWeight:700}}>{t}</div>
                <div style={{fontSize:11,color:'#888'}}>{r.category} · {r.question_count} questions</div>
            </div>
        )},
        { title:'Status', dataIndex:'status', width:110,
            render: v => <Tag color={v==='published'?'green':v==='draft'?'default':'red'}>{v}</Tag>
        },
        { title:'Responses', dataIndex:'response_count', width:100,
            render: v => <span style={{fontWeight:700,color:'#1890ff'}}>{v||0}</span>
        },
        { title:'', width:120, render:(_, r) => r.status==='published' && (
            <Button size="small" type="primary" icon={<FormOutlined/>}
                onClick={()=>openSurvey(r)}>Fill Survey</Button>
        )},
    ];

    return (
        <div className="page-content">
            {/* Header */}
            <div className="page-head" style={{background:'linear-gradient(135deg,#0d2137,#1e3a5f)',padding:'20px 24px'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:20,flexWrap:'wrap'}}>
                    <div style={{flex:1}}>
                        <div style={{fontSize:11,color:'rgba(255,255,255,0.6)',fontWeight:600,textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>
                            MoH Programme
                        </div>
                        <h2 style={{color:'#fff',margin:0,fontSize:24}}>{program.name}</h2>
                        <div style={{color:'rgba(255,255,255,0.7)',marginTop:4}}>
                            {program.code} · {program.category}
                        </div>
                        {program.parent_unit && (
                            <div style={{color:'rgba(255,255,255,0.5)',fontSize:12,marginTop:2}}>
                                Under: {program.parent_unit}
                            </div>
                        )}
                    </div>
                    {latestAssessment && (
                        <div style={{textAlign:'right'}}>
                            <div style={{fontSize:48,fontWeight:900,color:bandColor,lineHeight:1}}>
                                {score.toFixed(1)}%
                            </div>
                            <Tag style={{background:bandColor,color:'#fff',border:'none',fontSize:12,padding:'2px 10px',marginTop:4}}>
                                {band}
                            </Tag>
                        </div>
                    )}
                </div>
            </div>

            <div className="page-pad">
                {/* Summary KPIs */}
                <Row gutter={12} style={{marginBottom:20}}>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Total Assessments" value={assessments.length}
                                valueStyle={{color:'#1890ff',fontWeight:800}}/>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Approved" value={assessments.filter(a=>a.status==='approved').length}
                                valueStyle={{color:'#16a34a',fontWeight:800}}/>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Active Surveys" value={surveys.filter(s=>s.status==='published').length}
                                valueStyle={{color:'#7c3aed',fontWeight:800}}/>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="Survey Responses" value={surveys.reduce((s,x)=>s+parseInt(x.response_count||0),0)}
                                valueStyle={{color:'#d97706',fontWeight:800}}/>
                        </Card>
                    </Col>
                </Row>

                {/* Tabs */}
                <div style={{display:'flex',gap:0,borderBottom:'2px solid #f0f0f0',marginBottom:20}}>
                    {[
                        {key:'overview',    label:'Overview',    icon:<TrophyOutlined/>},
                        {key:'assessments', label:'Assessments', icon:<FileTextOutlined/>},
                        {key:'surveys',     label:'Surveys',     icon:<FormOutlined/>},
                    ].map(t=>(
                        <div key={t.key} onClick={()=>setActiveTab(t.key)}
                            style={{padding:'10px 20px',cursor:'pointer',fontSize:13,fontWeight:600,
                                display:'flex',alignItems:'center',gap:6,
                                color:activeTab===t.key?'#1890ff':'#555',
                                borderBottom:activeTab===t.key?'2px solid #1890ff':'2px solid transparent',
                                marginBottom:-2}}>
                            {t.icon} {t.label}
                        </div>
                    ))}
                </div>

                {/* Overview tab */}
                {activeTab === 'overview' && (
                    <div>
                        {latestAssessment ? (
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Card title="Latest Assessment" size="small">
                                        <div style={{textAlign:'center',padding:20}}>
                                            <div style={{fontSize:60,fontWeight:900,color:bandColor}}>{score.toFixed(1)}%</div>
                                            <Tag style={{background:bandColor,color:'#fff',border:'none',fontSize:14,padding:'4px 16px',marginTop:8}}>{band}</Tag>
                                            <div style={{marginTop:16}}>
                                                <Progress percent={score} strokeColor={bandColor} showInfo={false}/>
                                            </div>
                                            <div style={{marginTop:12,color:'#888',fontSize:12}}>
                                                Period: {latestAssessment.period_label} · {latestAssessment.assessment_date}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Card title="Performance History" size="small">
                                        {assessments.filter(a=>a.status==='approved').map((a,i)=>(
                                            <div key={a.id} style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
                                                <div style={{width:80,fontSize:11,color:'#888'}}>{a.period_label}</div>
                                                <Progress percent={parseFloat(a.overall_percentage||0)}
                                                    strokeColor={BANDS[a.program_band]||'#888'}
                                                    style={{flex:1}} size="small"/>
                                                <div style={{width:50,fontWeight:700,textAlign:'right',
                                                    color:BANDS[a.program_band]||'#888'}}>
                                                    {parseFloat(a.overall_percentage||0).toFixed(1)}%
                                                </div>
                                            </div>
                                        ))}
                                        {assessments.filter(a=>a.status==='approved').length===0 && (
                                            <Empty description="No approved assessments yet"/>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        ) : (
                            <Empty description="No assessments found for this programme"/>
                        )}
                    </div>
                )}

                {/* Assessments tab */}
                {activeTab === 'assessments' && (
                    <Table rowKey="id" dataSource={assessments} columns={assessmentCols}
                        size="small" pagination={{pageSize:10}}
                        locale={{emptyText:'No assessments yet'}}/>
                )}

                {/* Surveys tab */}
                {activeTab === 'surveys' && (
                    <div>
                        {surveys.length === 0 ? (
                            <Empty description="No surveys linked to this programme yet">
                                <div style={{fontSize:12,color:'#888',marginTop:8}}>
                                    Go to Survey Builder and link a survey to this programme
                                </div>
                            </Empty>
                        ) : (
                            <Table rowKey="id" dataSource={surveys} columns={surveyCols}
                                size="small" pagination={{pageSize:10}}/>
                        )}
                    </div>
                )}
            </div>

            {/* Survey form modal */}
            <Modal title={activeSurvey?.title} open={surveyModal}
                onCancel={()=>setSurveyModal(false)} footer={null}
                width={760} destroyOnClose>
                {activeSurvey && (
                    <QoCSurveyForm surveyId={activeSurvey.id}
                        onComplete={()=>{ setSurveyModal(false); loadAll(); }}/>
                )}
            </Modal>
        </div>
    );
}
